import { Box, Stack, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { KeyTitlesSortableList } from "./KeyTitles";
import { CustomToast } from "../ui/CustomToast";
import { campaignsApi, useUpdateKeyRoleMutation } from "../../api";
import { KeyRole } from "../../types/apiTypes";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { RestartAnalysisModal } from "../ui/modal";
import { setIsNotificationsOpen } from "../../features/companySlice";
import { useAppDispatch } from "../../hooks";
import { BuyingGroup } from "./BuyingGroup";
import { QuestionMarkInCircleIcon } from "../images/QuestionMarkInCircleIcon";
import { ReuseTooltip } from "../ui";

type Props = {
  role: KeyRole;
  campaign_id: string;
  isEditor?: boolean;
};

export const CommitteeRoleItem = ({ role, campaign_id, isEditor }: Props) => {
  const dispatch = useAppDispatch();
  const [isRestartModal, setIsRestartModal] = useState(false);
  const [updateKeyRole, { isError, error, isLoading }] =
    useUpdateKeyRoleMutation();

  const [isActive, setIsActive] = useState(role.is_active);
  const [prevValue, setPrevValue] = useState(role.is_active);

  const handleChange = (withAnalyze: boolean = true) => {
    const newValue = !isActive;
    setPrevValue(isActive);
    setIsActive(newValue);

    updateKeyRole({ id: role.id, is_active: newValue, campaign_id })
      .unwrap()
      .then(() => {
        if (withAnalyze) {
          dispatch(campaignsApi.util.invalidateTags(["activities"]));
          dispatch(setIsNotificationsOpen(true));
        }
        setIsRestartModal(false);
      });
  };

  useEffect(() => {
    if (isError && error) {
      setIsActive(prevValue);

      toast.error(
        <CustomToast
          type="error"
          text={
            getErrorMessage(error).globalError || "Failed to update key role"
          }
          title="Update Failed"
        />,
      );
    }
  }, [isError]);

  const openRestartAnalysisModalHandler = () => {
    if (role.accounts_to_reanalyze_count > 0) {
      setIsRestartModal(true);
    } else {
      handleChange(false);
    }
  };

  return (
    <Stack
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "6px",
        padding: "12px 16px 16px",
        gap: "12px",
      }}
    >
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "8px",
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        <Stack direction="row" alignItems="center">
          {isEditor ? (
            <Switch
              checked={isActive}
              onChange={openRestartAnalysisModalHandler}
              inputProps={{ "aria-label": "controlled" }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#1C3C6C",
                  "&:hover": {
                    backgroundColor: "#1C3C6C80",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#1C3C6C80",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                minWidth: "5px",
                minHeight: "5px",
                backgroundColor: "#1F2932",
                borderRadius: "50%",
                marginRight: "8px",
              }}
            />
          )}

          <Stack direction="row" alignItems="center" gap="4px">
            <ReuseTooltip
              maxWidth={"200px"}
              text={!role.weight && "Role not available for this company size."}
              hideChildren={false}
            >
              <Typography variant="subtitle1" color="#1F2932" fontWeight={500}>
                {role.label}
              </Typography>
            </ReuseTooltip>

            <ReuseTooltip maxWidth={"200px"} text={role.tooltip}>
              <QuestionMarkInCircleIcon
                width="20px"
                height="20px"
                color="#9B9B9B"
              />
            </ReuseTooltip>
          </Stack>
        </Stack>
        <BuyingGroup group={role.group} />
      </Stack>

      <KeyTitlesSortableList
        role={role}
        campaign_id={campaign_id}
        isEditor={isEditor}
      />
      <RestartAnalysisModal
        totalCompanies={role.accounts_to_reanalyze_count}
        onSubmit={handleChange}
        isOpen={isRestartModal}
        onClose={() => setIsRestartModal(false)}
        isLoading={isLoading}
      />
    </Stack>
  );
};
